import classNames from 'classnames';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Input } from '@eika/input';
import { StandardButton } from '@eika/button';
import DropDown from '../../../drop-down';
import ErrorMessage from '../../errormessage';
import CountryContext from '../../../country-context';
import getLabel, { getHelpBody } from '../labels';
import RadioInput from './radioinput';
import RrMultipleRelations from './rr-multiple-relations';
import Textarea from './textarea';
import { getSkatterelasjonFields } from './validation';
import { getCitizenshipList } from '../../helper/component-helper';
import { CITIZENSHIP, NORWAY, POSTAL_CODE, RESIDENCE_COUNTRY, SECOND_CITIZENSHIP } from '../keys-constants';
import RrRelationsUsa from './rr-relations-usa';
import RrPep from './rr-pep';

const ReellRettighetshaver = ({
    config = {},
    getHasSubmitted,
    ekstratekster,
    errors,
    index,
    listLength,
    onChange,
    onRemove,
    question,
    showErrors,
    instruks,
    appNamespace,
}) => {
    const hasInvalidValue = getHasSubmitted() && errors.length > 0;
    const countryContext = useContext(CountryContext);
    const fodelandOptions = useMemo(() => countryContext.getFodelandOptions(), [countryContext]);
    const bostedslandOptions = useMemo(() => countryContext.getBostedslandOptions(), [countryContext]);
    const skatterelasjonLandOptions = useMemo(() => countryContext.getCRSOptions(), [countryContext]);
    const norwayPostalCodesList = useMemo(() => countryContext.getNorwegianPostalCodesOptions, [countryContext]);

    const [citizenship, setCitizenship] = useState(question.statsborgerskap);
    const [secondCitizenship, setSecondCitizenship] = useState(question.sekundartStatsborgerskap);
    const [residenceCountry, setResidenceCountry] = useState(question.bostedsland);
    const [postalCode, setPostalCode] = useState(question.postnummer);
    const listOfCountries = countryContext.getFodelandOptions();

    const getHandleChange = useCallback(
        (key, dataKey = 'value') =>
            (event) => {
                let newVal = event?.target?.[dataKey] ?? event;
                if (event?.target?.name?.includes('eierandelProsent')) {
                    newVal = `${newVal}`.replace(',', '.').replace(new RegExp(/[^0-9.]/, 'g'), '');
                    if (newVal.toString().length >= 5) newVal = parseFloat(`${newVal}`).toFixed(2);
                }
                if (event?.target?.name?.includes('fnr')) {
                    newVal = `${newVal}`.replace(new RegExp(/\s/, 'g'), '');
                }
                // support boolean values in radios
                if (dataKey === 'checked') {
                    newVal = event?.target?.value !== 'false';
                }
                if (key === CITIZENSHIP) {
                    setCitizenship(newVal);
                }
                if (key === SECOND_CITIZENSHIP) {
                    setSecondCitizenship(newVal);
                }
                if (key === RESIDENCE_COUNTRY && newVal !== '') {
                    setResidenceCountry(newVal);
                }
                if (key === POSTAL_CODE && newVal !== '') {
                    setPostalCode(newVal);
                }
                onChange({ [key]: newVal });
            },
        [onChange]
    );

    const removeValue = useCallback(
        (key) => {
            if (key === CITIZENSHIP) {
                setCitizenship(undefined);
            }
            if (key === SECOND_CITIZENSHIP) {
                setSecondCitizenship(undefined);
            }
            if (key === RESIDENCE_COUNTRY) {
                setResidenceCountry(undefined);
            }
            onChange({ [key]: undefined });
        },
        [onChange]
    );

    const addSkatterelasjon = useCallback(() => {
        onChange({
            skatterelasjoner: [...question.skatterelasjoner, getSkatterelasjonFields()],
        });
    }, [onChange, question.skatterelasjoner]);

    const generateErrorTextForCumulativeOwnership = () => {
        if (errors.includes('eierandelProsent')) {
            return getLabel(ekstratekster, 'rr_eierandelProsent_instruks');
        }
        if (errors.includes('eierandelProsentCumulated')) {
            return getLabel(ekstratekster, 'rr_eierandelProsentCumulated_instruks');
        }
        return null;
    };
    const isCountryNorway = (val) => val === NORWAY;

    const errorTextPostalCode = () => {
        if (errors.includes('postnummer')) {
            return getLabel(ekstratekster, 'rr_postnummer_instruks');
        }
        if (
            getHasSubmitted() &&
            isCountryNorway(residenceCountry) &&
            norwayPostalCodesList &&
            !norwayPostalCodesList.includes(postalCode)
        ) {
            return getLabel(ekstratekster, 'rr_norwegian_postal_code_instruks');
        }
        return null;
    };

    return (
        <div
            className={classNames('rr-answer', { 'skjema-input-field-wrap-small skjema-input-field-wrap': index > 0 })}
        >
            <div className="rr-header" style={{ fontSize: '20px' }}>
                <>
                    {`${getLabel(ekstratekster, 'rr_tittel')} ${index + 1}`}
                    {listLength > 1 && <StandardButton className="removeicon" onClick={onRemove} />}
                </>
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('navn') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <Input
                    type="text"
                    className="rr-navn form-control"
                    name={`navn-${question.rrId}`}
                    onChange={getHandleChange('navn')}
                    label={getLabel(ekstratekster, 'rr_navn')}
                    value={question.navn || ''}
                    modalHelpHeader={ekstratekster.rr_navn_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_navn_info')}
                />
                {errors.includes('navn') && <ErrorMessage text={getLabel(ekstratekster, 'rr_navn_instruks')} />}
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('fnr') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <Input
                    className="rr-fnr"
                    name={`fnr-${question.rrId}`}
                    label={getLabel(ekstratekster, 'rr_fnr')}
                    type="tel"
                    onChange={getHandleChange('fnr')}
                    value={question.fnr || ''}
                    placeholder={ekstratekster.rr_fnr_placeholder}
                    modalHelpHeader={ekstratekster.rr_fnr_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_fnr_info')}
                />
                {errors.includes('fnr') && <ErrorMessage text={getLabel(ekstratekster, 'rr_fnr_instruks')} />}
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('fodeland') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <DropDown
                    name={`birth-country-rr-${question.rrId}`}
                    values={fodelandOptions}
                    selected={question.fodeland || ''}
                    selectValue={getHandleChange('fodeland')}
                    errorText={errors.includes('fodeland') ? getLabel(ekstratekster, 'rr_fodeland_instruks') : null}
                    modalHelpHeader={ekstratekster.rr_fodeland_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_fodeland_info')}
                    removeValue={() => removeValue('fodeland')}
                    label={getLabel(ekstratekster, 'rr_fodeland')}
                />
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('statsborgerskap') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <DropDown
                    name={`citizenship-rr-${question.rrId}`}
                    values={getCitizenshipList(listOfCountries, secondCitizenship)}
                    selected={question.statsborgerskap || ''}
                    selectValue={getHandleChange('statsborgerskap')}
                    errorText={
                        errors.includes('statsborgerskap')
                            ? getLabel(ekstratekster, 'rr_statsborgerskap_instruks')
                            : null
                    }
                    modalHelpHeader={ekstratekster.rr_statsborgerskap_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_statsborgerskap_info')}
                    removeValue={() => removeValue('statsborgerskap')}
                    label={getLabel(ekstratekster, 'rr_statsborgerskap')}
                />
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('sekundartStatsborgerskap') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <DropDown
                    name={`second-citizenship-rr-${question.rrId}`}
                    values={getCitizenshipList(listOfCountries, citizenship)}
                    selected={question.sekundartStatsborgerskap || ''}
                    selectValue={getHandleChange('sekundartStatsborgerskap')}
                    modalHelpHeader={ekstratekster.rr_statsborgerskap2_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_statsborgerskap2_info')}
                    removeValue={() => removeValue('sekundartStatsborgerskap')}
                    label={getLabel(ekstratekster, 'rr_statsborgerskap2')}
                />
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('adresse') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <Input
                    type="text"
                    label={getLabel(ekstratekster, 'rr_adresse', config.addressInformationOptional)}
                    className="rr-adresse"
                    name={`adresse-${question.rrId}`}
                    onChange={getHandleChange('adresse')}
                    value={question.adresse || ''}
                    modalHelpHeader={ekstratekster.rr_adresse_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_adresse_info')}
                />
                {errors.includes('adresse') && <ErrorMessage text={getLabel(ekstratekster, 'rr_adresse_instruks')} />}
            </div>
            <div className="row row-no-gutters skjema-input-field-wrap-small skjema-input-field-wrap">
                <div className={classNames('col-xs-4', { 'has-error error': errors.includes('postnummer') })}>
                    <Input
                        label={getLabel(ekstratekster, 'rr_postnummer', config.addressInformationOptional)}
                        className="rr-postnummer"
                        name={`postnummer-${question.rrId}`}
                        type="tel"
                        onChange={getHandleChange('postnummer')}
                        value={question.postnummer || ''}
                        modalHelpHeader={ekstratekster.rr_postnummer_infotittel}
                        modalHelpBody={getHelpBody(ekstratekster, 'rr_postnummer_info')}
                    />
                    {errorTextPostalCode() && <ErrorMessage text={errorTextPostalCode()} />}
                </div>
                <div
                    className={classNames('col-xs-7 col-xs-offset-1', {
                        'has-error error': errors.includes('poststed'),
                    })}
                >
                    <Input
                        type="text"
                        className="rr-poststed"
                        label={getLabel(ekstratekster, 'rr_poststed', config.addressInformationOptional)}
                        name={`poststed-${question.rrId}`}
                        onChange={getHandleChange('poststed')}
                        value={question.poststed || ''}
                        modalHelpHeader={ekstratekster.rr_poststed_infotittel}
                        modalHelpBody={getHelpBody(ekstratekster, 'rr_poststed_info')}
                    />
                    {errors.includes('poststed') && (
                        <ErrorMessage text={getLabel(ekstratekster, 'rr_poststed_instruks')} />
                    )}
                </div>
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('bostedsland') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <DropDown
                    name={`residence-rr-${question.rrId}`}
                    values={bostedslandOptions}
                    selected={question.bostedsland || ''}
                    selectValue={getHandleChange('bostedsland')}
                    errorText={
                        errors.includes('bostedsland') ? getLabel(ekstratekster, 'rr_bostedsland_instruks') : null
                    }
                    modalHelpHeader={ekstratekster.rr_bostedsland_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_bostedsland_info')}
                    removeValue={() => removeValue('bostedsland')}
                    label={getLabel(ekstratekster, 'rr_bostedsland', config.addressInformationOptional)}
                />
            </div>
            <div
                className={classNames(
                    { 'has-error error': errors.includes('epost') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <Input
                    type="text"
                    className="rr-epost"
                    label={getLabel(ekstratekster, 'rr_epost', true)}
                    name={`epost-${question.rrId}`}
                    onChange={getHandleChange('epost')}
                    value={question.epost || ''}
                    modalHelpHeader={ekstratekster.rr_epost_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_epost_info')}
                />
                {errors.includes('epost') && <ErrorMessage text={getLabel(ekstratekster, 'rr_epost_instruks')} />}
            </div>

            <div
                className={classNames(
                    { 'has-error error': errors.includes('mobil') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <Input
                    className="rr-mobil"
                    label={getLabel(ekstratekster, 'rr_mobil', true)}
                    name={`mobil-${question.rrId}`}
                    type="tel"
                    onChange={getHandleChange('mobil')}
                    value={question.mobil || ''}
                    modalHelpHeader={ekstratekster.rr_mobil_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_mobil_info')}
                />
                {errors.includes('mobil') && <ErrorMessage text={getLabel(ekstratekster, 'rr_mobil_instruks')} />}
            </div>
            <div
                className={classNames(
                    {
                        'has-error error':
                            errors.includes('eierandelProsent') || errors.includes('eierandelProsentCumulated'),
                    },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <Input
                    className="rr-eierandelProsent"
                    label={getLabel(ekstratekster, 'rr_eierandelProsent')}
                    name={`eierandelProsent-${question.rrId}`}
                    type="tel"
                    onChange={getHandleChange('eierandelProsent')}
                    value={question.eierandelProsent || ''}
                    modalHelpHeader={ekstratekster.rr_eierandelProsent_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_eierandelProsent_info')}
                />
                {errors.includes('eierandelProsent') && (
                    <ErrorMessage text={generateErrorTextForCumulativeOwnership()} />
                )}
            </div>

            <div
                className={classNames(
                    { 'has-error error': errors.includes('bestemmendeKontroll') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <RadioInput
                    className="rr-bestemmendeKontroll"
                    heading={getLabel(ekstratekster, 'rr_kontroll')}
                    id={question.rrId}
                    labels={['Ja', 'Nei']}
                    name={`bestemmendeKontroll-${index}`}
                    onChange={getHandleChange('bestemmendeKontroll', 'checked')}
                    values={[true, false]}
                    value={question.bestemmendeKontroll}
                    errorText={
                        errors.includes('bestemmendeKontroll')
                            ? getLabel(ekstratekster, 'rr_bestemmendeKontroll_instruks')
                            : null
                    }
                    modalHelpHeader={ekstratekster.rr_bestemmendeKontroll_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_bestemmendeKontroll_info')}
                    appNamespace={appNamespace}
                />
            </div>
            {question.bestemmendeKontroll && (
                <div className={classNames({ 'has-error error': errors.includes('bestemmendeKontrollType') })}>
                    <Textarea
                        className="rr-bestemmendeKontrollType"
                        label={getLabel(ekstratekster, 'rr_kontrolltype')}
                        id={question.rrId}
                        name={`bestemmendeKontrollType-${index}`}
                        onChange={getHandleChange('bestemmendeKontrollType')}
                        value={question.bestemmendeKontrollType || ''}
                        // TODO: Expand Textarea to support tooltipText
                        tooltipText={getLabel(ekstratekster, 'rr_kontroll_info')}
                        maxLength="200"
                        errorText={
                            errors.includes('bestemmendeKontrollType')
                                ? getLabel(ekstratekster, 'rr_bestemmendeKontrollType_instruks')
                                : null
                        }
                        modalHelpHeader={ekstratekster.rr_bestemmendeKontrollType_infotittel}
                        modalHelpBody={getHelpBody(ekstratekster, 'rr_bestemmendeKontrollType_info')}
                    />
                </div>
            )}
            <div
                className={classNames(
                    { 'has-error error': errors.includes('pep') },
                    'skjema-input-field-wrap-small skjema-input-field-wrap'
                )}
            >
                <RadioInput
                    className="rr-pep"
                    heading={getLabel(ekstratekster, 'rr_pep')}
                    id={question.rrId}
                    labels={['Ja', 'Nei']}
                    name={`pep-rr-${index}`}
                    onChange={getHandleChange('pep', 'checked')}
                    values={[true, false]}
                    value={question.pep}
                    errorText={errors.includes('pep') ? getLabel(ekstratekster, 'rr_pep_instruks') : null}
                    modalHelpHeader={ekstratekster.rr_pep_infotittel}
                    modalHelpBody={getHelpBody(ekstratekster, 'rr_pep_info')}
                />
            </div>
            {question.pep && (
                <RrPep
                    question={question}
                    getLabel={getLabel}
                    getHandleChange={getHandleChange}
                    errors={errors}
                    ekstratekster={ekstratekster}
                    getHelpBody={getHelpBody}
                />
            )}
            {!config.rrHideFatca && (
                <div
                    className={classNames(
                        { 'has-error error': errors.includes('skatterelasjonUsa') },
                        'skjema-input-field-wrap-small skjema-input-field-wrap'
                    )}
                >
                    <RadioInput
                        className="rr-skatterelasjonUsa"
                        heading={getLabel(ekstratekster, 'rr_fatca')}
                        id={question.rrId}
                        labels={['Ja', 'Nei']}
                        name={`skatterelasjonUsa-${index}`}
                        onChange={getHandleChange('skatterelasjonUsa', 'checked')}
                        values={[true, false]}
                        value={question.skatterelasjonUsa}
                        errorText={
                            errors.includes('skatterelasjonUsa') ? getLabel(ekstratekster, 'rr_fatca_instruks') : null
                        }
                        modalHelpHeader={ekstratekster.rr_fatca_infotittel}
                        modalHelpBody={getHelpBody(ekstratekster, 'rr_fatca_info')}
                    />
                </div>
            )}
            {!config.rrHideFatca && question.skatterelasjonUsa && (
                <RrRelationsUsa
                    question={question}
                    getLabel={getLabel}
                    getHandleChange={getHandleChange}
                    errors={errors}
                    ekstratekster={ekstratekster}
                    getHelpBody={getHelpBody}
                    index={index}
                    hasInvalidValue={hasInvalidValue}
                />
            )}
            {!config.rrHideCRS && (
                <div
                    className={classNames(
                        { 'has-error error': errors.includes('skatterelasjonerFlere') },
                        'skjema-input-field-wrap-small skjema-input-field-wrap'
                    )}
                >
                    <RadioInput
                        className="rr-skatterelasjonerFlere"
                        heading={getLabel(ekstratekster, 'rr_crs')}
                        id={question.rrId}
                        labels={['Ja', 'Nei']}
                        name={`skatterelasjonerFlere-${index}`}
                        onChange={getHandleChange('skatterelasjonerFlere', 'checked')}
                        values={[true, false]}
                        value={question.skatterelasjonerFlere}
                        errorText={
                            errors.includes('skatterelasjonerFlere') ? getLabel(ekstratekster, 'rr_crs_instruks') : null
                        }
                        modalHelpHeader={ekstratekster.rr_skatterelasjonerFlere_infotittel}
                        modalHelpBody={getHelpBody(ekstratekster, 'rr_skatterelasjonerFlere_info')}
                    />
                </div>
            )}
            {!config.rrHideCRS && question.skatterelasjonerFlere && (
                <RrMultipleRelations
                    question={question}
                    countryContext={countryContext}
                    ekstratekster={ekstratekster}
                    addSkatterelasjon={addSkatterelasjon}
                    skatterelasjonLandOptions={skatterelasjonLandOptions}
                    showErrors={showErrors}
                    onChange={onChange}
                    getLabel={getLabel}
                />
            )}
            {hasInvalidValue && <ErrorMessage text={instruks} />}
        </div>
    );
};

export const rrConfigShape = shape({
    rrHideFatca: bool,
    rrHideCRS: bool,
    addressInformationOptional: bool,
});

export const ekstrateksterShape = shape({
    rr_adresse_instruks: string,
    rr_adresse_info: string,
    rr_adresse_infotittel: string,
    rr_adresse: string,
    rr_bestemmendeKontroll_instruks: string,
    rr_bestemmendeKontroll_info: string,
    rr_bestemmendeKontroll_infotittel: string,
    rr_bestemmendeKontrollType_instruks: string,
    rr_bestemmendeKontrollType_info: string,
    rr_bestemmendeKontrollType_infotittel: string,
    rr_bostedsland_instruks: string,
    rr_bostedsland_info: string,
    rr_bostedsland_infotittel: string,
    rr_bostedsland: string,
    rr_crs_instruks: string,
    rr_crs: string,
    rr_crsflere: string,
    rr_crsland: string,
    rr_crsmangler: string,
    rr_crsnummer: string,
    rr_eierandel: string,
    rr_eierandelProsent_instruks: string,
    rr_eierandelProsent_info: string,
    rr_eierandelProsent_infotittel: string,
    rr_epost_instruks: string,
    rr_epost_info: string,
    rr_epost_infotittel: string,
    rr_epost: string,
    rr_fatca_instruks: string,
    rr_fatca_info: string,
    rr_fatca_infotittel: string,
    rr_fatca: string,
    rr_fatcaIkkeTIN_instruks: string,
    rr_fatcaIkkeTIN_info: string,
    rr_fatcaIkkeTIN_infotittel: string,
    rr_fatcaIkkeTIN: string,
    rr_fatcaTIN_instruks: string,
    rr_fatcaTIN: string,
    rr_flere_label: string,
    rr_flere: string,
    rr_fnr_instruks: string,
    rr_fnr_info: string,
    rr_fnr_infotittel: string,
    rr_fnr: string,
    rr_fodeland_instruks: string,
    rr_fodeland_info: string,
    rr_fodeland_infotittel: string,
    rr_fodeland: string,
    rr_kontroll_info: string,
    rr_kontroll: string,
    rr_kontrolltype: string,
    rr_mobil_instruks: string,
    rr_mobil_info: string,
    rr_mobil_infotittel: string,
    rr_mobil: string,
    rr_navn_info: string,
    rr_navn_infotittel: string,
    rr_navn: string,
    rr_pep_instruks: string,
    rr_pep_info: string,
    rr_pep_infotittel: string,
    rr_pep: string,
    rr_pepRelasjon_instruks: string,
    rr_pepRelasjon_info: string,
    rr_pepRelasjon_infotittel: string,
    rr_peprelasjon: string,
    rr_peptype: string,
    rr_pepVerv_instruks: string,
    rr_pepVerv_info: string,
    rr_pepVerv_infotittel: string,
    rr_postnummer_instruks: string,
    rr_postnummer_info: string,
    rr_postnummer_infotittel: string,
    rr_postnummer: string,
    rr_poststed_instruks: string,
    rr_poststed_info: string,
    rr_poststed_infotittel: string,
    rr_poststed: string,
    rr_skatterelasjonerFlere_info: string,
    rr_skatterelasjonerFlere_infotittel: string,
    rr_statsborgerskap_instruks: string,
    rr_statsborgerskap_info: string,
    rr_statsborgerskap_infotittel: string,
    rr_statsborgerskap: string,
    rr_statsborgerskap2_info: string,
    rr_statsborgerskap2_infotittel: string,
    rr_statsborgerskap2: string,
    rr_tittel: string,
});

ReellRettighetshaver.propTypes = {
    appNamespace: string,
    config: rrConfigShape,
    getHasSubmitted: func.isRequired,
    ekstratekster: ekstrateksterShape,
    errors: array,
    index: number.isRequired,
    listLength: number.isRequired,
    onChange: func,
    onRemove: func,
    question: object.isRequired,
    showErrors: bool,
    instruks: string.isRequired,
};

export default ReellRettighetshaver;
